import React, { useContext } from 'react';
import { Typography, Box, Grid, Container, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AuthContext from '../contexts/AuthContext';
import { useQuery, gql } from '@apollo/client';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from 'recharts';
import * as XLSX from 'xlsx';
import {
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { AnimatedPaper, StyledTableContainer, StyledButton, tableRowHoverStyles, exportIconButtonStyles } from '../components/StyledComponents';


const DASHBOARD_STATS = gql`
  query DashboardStats {
    dashboardStats {
      visitsByDepartment {
        departmentId
        departmentName
        total
        approved
        pending
        rejected
        completed
      }
      visitsByHost {
        hostId
        hostName
        total
        approved
        pending
        rejected
        completed
      }
      visitsByVisitor {
        visitorId
        visitorName
        total
        approved
        pending
        rejected
        completed
      }
      visitsByFunction {
        functionId
        functionName
        departmentId
        departmentName
        total
        approved
        pending
        rejected
        completed
      }
    }
  }
`;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

function Dashboard() {
  const { user } = useContext(AuthContext);
  const { data, loading, error } = useQuery(DASHBOARD_STATS);

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <Alert severity="error">Erreur: {error.message}</Alert>;

  const { visitsByDepartment, visitsByHost, visitsByVisitor, visitsByFunction } = data.dashboardStats;

  // Fonction pour exporter un tableau en Excel
  const exportToExcel = (data, filename, headers) => {
    // Préparer les données avec les en-têtes
    const worksheetData = [
      headers,
      ...data.map(row => {
        const rowData = [];
        headers.forEach(header => {
          // Mapper les clés selon les headers dans l'ordre
          switch(header) {
            case 'Département':
              rowData.push(row.departmentName || '');
              break;
            case 'Hôte':
              rowData.push(row.hostName || '');
              break;
            case 'Visiteur':
              rowData.push(row.visitorName || '');
              break;
            case 'Fonction':
              rowData.push(row.functionName || '');
              break;
            case 'Total':
              rowData.push(row.total || 0);
              break;
            case 'Approuvée':
              rowData.push(row.approved || 0);
              break;
            case 'En attente':
              rowData.push(row.pending || 0);
              break;
            case 'Rejetée':
              rowData.push(row.rejected || 0);
              break;
            case 'Terminée':
              rowData.push(row.completed || 0);
              break;
            default:
              rowData.push('');
          }
        });
        return rowData;
      })
    ];

    // Créer un workbook et une feuille
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Données');

    // Télécharger le fichier
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Fonctions d'export pour chaque tableau
  const exportDepartmentTable = () => {
    exportToExcel(
      visitsByDepartment,
      'Statistiques_par_departement',
      ['Département', 'Total', 'Approuvée', 'En attente', 'Rejetée', 'Terminée']
    );
  };

  const exportHostTable = () => {
    exportToExcel(
      visitsByHost,
      'Statistiques_par_hote',
      ['Hôte', 'Total', 'Approuvée', 'En attente', 'Rejetée', 'Terminée']
    );
  };

  const exportVisitorTable = () => {
    exportToExcel(
      visitsByVisitor,
      'Statistiques_par_visiteur',
      ['Visiteur', 'Total', 'Approuvée', 'En attente', 'Rejetée', 'Terminée']
    );
  };

  const exportFunctionTable = () => {
    exportToExcel(
      visitsByFunction,
      'Statistiques_par_fonction',
      ['Fonction', 'Département', 'Total', 'Approuvée', 'En attente', 'Rejetée', 'Terminée']
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>Tableau de bord des visites</Typography>
      <Grid container spacing={4}>
        {/* 1. Nombre total de visites par département */}
        <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Nombre total de visites du département</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByDepartment} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="departmentName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#1976d2" name="Total">
                  <LabelList dataKey="total" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid>
        {/* 2. Visites par statut et département */}
        <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Visites par statut et département</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByDepartment} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="departmentName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" fill="#4caf50" name="Approuvée">
                  <LabelList dataKey="approved" position="top" />
                </Bar>
                <Bar dataKey="pending" fill="#ff9800" name="En attente">
                  <LabelList dataKey="pending" position="top" />
                </Bar>
                <Bar dataKey="rejected" fill="#f44336" name="Rejetée">
                  <LabelList dataKey="rejected" position="top" />
                </Bar>
                <Bar dataKey="completed" fill="#2196f3" name="Terminée">
                  <LabelList dataKey="completed" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid>
        {/* 3. Nombre total de visites par hôte */}
        <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Nombre total de visites par hôte</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByHost} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="hostName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#1976d2" name="Total">
                  <LabelList dataKey="total" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid>
        {/* 4. Visites par statut et hôte */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Visites par statut et hôte</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByHost} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="hostName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" stackId="a" fill="#4caf50" name="Approuvée" />
                <Bar dataKey="pending" stackId="a" fill="#ff9800" name="En attente" />
                <Bar dataKey="rejected" stackId="a" fill="#f44336" name="Rejetée" />
                <Bar dataKey="completed" stackId="a" fill="#2196f3" name="Terminée" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid> */}
        {/* 5. Nombre total de visites par visiteur */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Nombre total de visites par visiteur</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByVisitor} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="visitorName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#1976d2" name="Total" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid> */}
        {/* 6. Visites par statut et visiteur */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Visites par statut et visiteur</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByVisitor} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="visitorName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" stackId="a" fill="#4caf50" name="Approuvée" />
                <Bar dataKey="pending" stackId="a" fill="#ff9800" name="En attente" />
                <Bar dataKey="rejected" stackId="a" fill="#f44336" name="Rejetée" />
                <Bar dataKey="completed" stackId="a" fill="#2196f3" name="Terminée" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid> */}
        {/* 7. Nombre total de visites par fonction */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Nombre total de visites par fonction</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByFunction} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="functionName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#1976d2" name="Total" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid> */}
        {/* 8. Visites par statut et fonction */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Visites par statut et fonction</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visitsByFunction} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="functionName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" stackId="a" fill="#4caf50" name="Approuvée" />
                <Bar dataKey="pending" stackId="a" fill="#ff9800" name="En attente" />
                <Bar dataKey="rejected" stackId="a" fill="#f44336" name="Rejetée" />
                <Bar dataKey="completed" stackId="a" fill="#2196f3" name="Terminée" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid> */}
      </Grid>
      {/* Statistiques récapitulatifs */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>Statistiques récapitulatifs</Typography>
        </Grid>
        <Grid item xs={12}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Statistique du département</Typography>
              <IconButton 
                color="primary" 
                onClick={exportDepartmentTable}
                size="small"
                title="Exporter en Excel"
                sx={exportIconButtonStyles}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Département</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Approuvée</TableCell>
                    <TableCell>En attente</TableCell>
                    <TableCell>Rejetée</TableCell>
                    <TableCell>Terminée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visitsByDepartment.map((row, index) => (
                    <TableRow 
                      key={row.departmentId}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        ...tableRowHoverStyles
                      }}
                    >
                      <TableCell>{row.departmentName}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.approved}</TableCell>
                      <TableCell>{row.pending}</TableCell>
                      <TableCell>{row.rejected}</TableCell>
                      <TableCell>{row.completed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </AnimatedPaper>
        </Grid>
        <Grid item xs={12}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Statistique par hôte</Typography>
              <IconButton 
                color="primary" 
                onClick={exportHostTable}
                size="small"
                title="Exporter en Excel"
                sx={exportIconButtonStyles}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Hôte</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Approuvée</TableCell>
                    <TableCell>En attente</TableCell>
                    <TableCell>Rejetée</TableCell>
                    <TableCell>Terminée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visitsByHost.map((row, index) => (
                    <TableRow 
                      key={row.hostId}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        ...tableRowHoverStyles
                      }}
                    >
                      <TableCell>{row.hostName}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.approved}</TableCell>
                      <TableCell>{row.pending}</TableCell>
                      <TableCell>{row.rejected}</TableCell>
                      <TableCell>{row.completed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </AnimatedPaper>
        </Grid>
        <Grid item xs={12}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Statistique par visiteur</Typography>
              <IconButton 
                color="primary" 
                onClick={exportVisitorTable}
                size="small"
                title="Exporter en Excel"
                sx={exportIconButtonStyles}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Visiteur</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Approuvée</TableCell>
                    <TableCell>En attente</TableCell>
                    <TableCell>Rejetée</TableCell>
                    <TableCell>Terminée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visitsByVisitor.map((row, index) => (
                    <TableRow 
                      key={row.visitorId}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        ...tableRowHoverStyles
                      }}
                    >
                      <TableCell>{row.visitorName}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.approved}</TableCell>
                      <TableCell>{row.pending}</TableCell>
                      <TableCell>{row.rejected}</TableCell>
                      <TableCell>{row.completed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </AnimatedPaper>
        </Grid>
        <Grid item xs={12}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Statistique par fonction</Typography>
              <IconButton 
                color="primary" 
                onClick={exportFunctionTable}
                size="small"
                title="Exporter en Excel"
                sx={exportIconButtonStyles}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fonction</TableCell>
                    <TableCell>Département</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Approuvée</TableCell>
                    <TableCell>En attente</TableCell>
                    <TableCell>Rejetée</TableCell>
                    <TableCell>Terminée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visitsByFunction.map((row, index) => (
                    <TableRow 
                      key={row.functionId}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        ...tableRowHoverStyles
                      }}
                    >
                      <TableCell>{row.functionName}</TableCell>
                      <TableCell>{row.departmentName}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.approved}</TableCell>
                      <TableCell>{row.pending}</TableCell>
                      <TableCell>{row.rejected}</TableCell>
                      <TableCell>{row.completed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </AnimatedPaper>
        </Grid>
      </Grid>
      <Box marginTop={4}>
        {/* <Typography variant="h5" gutterBottom>
          Bienvenue dans le système de Gestion Electronique des Visites et Absences Administratives
        </Typography>
        <Typography paragraph>
          Ce système vous permet de gérer efficacement les visites dans votre département.
          Utilisez le menu de navigation pour accéder aux différentes fonctionnalités.
        </Typography> */}
        
        {['ADMIN', 'MANAGER'].includes(user?.role) && (
          <StyledButton
            variant="contained"
            color="primary"
            component={Link}
            to="/users"
            startIcon={<AdminPanelSettingsIcon />}
            sx={{ mt: 2 }}
          >
            Administration
          </StyledButton>
        )}
      </Box>
    </Container>
  );
}

export default Dashboard; 